import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';

declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };
};

// Story 54.4 — the reCAPTCHA delegate of CaptchaService. The site key now
// comes from the discovery `captcha` member at runtime; the build-time
// environment key is only the default for a caller that passes none.
@Injectable({providedIn: 'root'})
export class RecaptchaService {
  private loaded = false;
  private loadPromise: Promise<void> | null = null;

  private loadScript(siteKey: string): Promise<void> {
    if (this.loaded) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.onload = () => {
        this.loaded = true;
        resolve();
      };
      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('reCAPTCHA script failed to load'));
      };
      document.head.appendChild(script);
    });
    return this.loadPromise;
  }

  async execute(action: string, siteKey: string = environment.recaptchaSiteKey): Promise<string> {
    await this.loadScript(siteKey);
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('reCAPTCHA initialization timed out')), 10000);
      grecaptcha.enterprise.ready(async () => {
        clearTimeout(timeout);
        try {
          const token = await grecaptcha.enterprise.execute(siteKey, {action});
          resolve(token);
        } catch (err) {
          reject(err);
        }
      });
    });
  }
}
